import { translate } from '../../i18n/index.js';
import { useState } from 'react';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

interface Props {
  onSuccess: () => void | Promise<void>;
  onLoadError?: (message: string) => void;
  returnUrl: string;
  customerEmail?: string | undefined;
  savePaymentMethod?: boolean;
  splitSubmit?: boolean;
}

export function StripePaymentForm({
  onSuccess,
  onLoadError,
  returnUrl,
  customerEmail,
  savePaymentMethod = false,
  splitSubmit = false,
}: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expressCheckoutAvailable, setExpressCheckoutAvailable] = useState(false);

  async function confirmPayment() {
    if (!stripe || !elements) return null;

    return stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
        ...(savePaymentMethod
          ? { payment_method_data: { allow_redisplay: 'always' as const } }
          : {}),
      },
      redirect: 'if_required',
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const result = await confirmPayment();
      if (!result) return;

      if (result.error) {
        setError(
          result.error.message ??
            translate(
              'components.payment.stripe_payment_form.handle_submit.set_error.error_payment',
            ),
        );
        setLoading(false);
      } else {
        await onSuccess();
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate(
              'components.payment.stripe_payment_form.handle_submit.set_error.error_payment_2',
            ),
      );
      setLoading(false);
    }
  }

  const paymentFields = (
    <>
      <ExpressCheckoutElement
        options={{
          buttonHeight: 48,
          buttonTheme: { applePay: 'black', googlePay: 'black' },
          buttonType: { applePay: 'book', googlePay: 'book' },
          layout: { maxColumns: 2, maxRows: 1, overflow: 'never' },
          paymentMethods: {
            applePay: 'always',
            googlePay: 'always',
            link: 'never',
            amazonPay: 'never',
            paypal: 'never',
          },
        }}
        onReady={(event) => {
          const methods = event.availablePaymentMethods;
          setExpressCheckoutAvailable(Boolean(methods?.applePay || methods?.googlePay));
        }}
        onLoadError={() => {
          setExpressCheckoutAvailable(false);
        }}
        onClick={(event) => {
          event.resolve({ emailRequired: true });
        }}
        onConfirm={async (event) => {
          if (!stripe || !elements) return;

          setLoading(true);
          setError(null);

          try {
            const result = await confirmPayment();

            if (result?.error) {
              event.paymentFailed({ reason: 'fail' });
              setError(
                result.error.message ??
                  translate(
                    'components.payment.stripe_payment_form.stripe_payment_form.express_checkout_element.on_confirm',
                  ),
              );
              setLoading(false);
            } else {
              await onSuccess();
            }
          } catch (err) {
            event.paymentFailed({ reason: 'fail' });
            setError(
              err instanceof Error
                ? err.message
                : translate(
                    'components.payment.stripe_payment_form.stripe_payment_form.express_checkout_element.on_confirm_2',
                  ),
            );
            setLoading(false);
          }
        }}
      />

      {expressCheckoutAvailable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--af-border)' }} />
          <span style={{ color: 'var(--af-muted)', fontSize: 12, fontWeight: 700 }}>
            {translate('components.payment.stripe_payment_form.stripe_payment_form.span.copy')}
          </span>
          <div style={{ flex: 1, height: 1, background: 'var(--af-border)' }} />
        </div>
      )}
      {customerEmail ? (
        <PaymentElement
          options={{
            defaultValues: { billingDetails: { email: customerEmail } },
            wallets: { applePay: 'auto', googlePay: 'auto', link: 'never' },
          }}
          onLoadError={(event) => {
            onLoadError?.(
              event.error.message ??
                translate(
                  'components.payment.stripe_payment_form.stripe_payment_form.payment_element.on_load_error',
                ),
            );
          }}
        />
      ) : (
        <PaymentElement
          options={{ wallets: { applePay: 'auto', googlePay: 'auto', link: 'never' } }}
          onLoadError={(event) => {
            onLoadError?.(
              event.error.message ??
                translate(
                  'components.payment.stripe_payment_form.stripe_payment_form.payment_element.on_load_error',
                ),
            );
          }}
        />
      )}
      {error && <p style={{ color: 'red', marginTop: 8 }}>{error}</p>}
    </>
  );

  return (
    <form onSubmit={(event) => void handleSubmit(event)}>
      {splitSubmit ? (
        <div className="af-card af-checkout-payment-card">{paymentFields}</div>
      ) : (
        paymentFields
      )}
      <div className="af-checkout-secure-note">
        {translate(
          'components.payment.stripe_payment_form.stripe_payment_form.div_checkout_secure_note.stripe',
        )}
      </div>
      <div className="af-checkout-submit-row" style={{ marginTop: splitSubmit ? 0 : 20 }}>
        <button
          className={`af-primary-btn af-checkout-submit${loading ? ' is-processing' : ''}`}
          type="submit"
          disabled={!stripe || loading}
          style={{ padding: 16, fontSize: 17 }}
        >
          <span className="af-processing-button-text">
            {loading
              ? translate(
                  'components.payment.stripe_payment_form.stripe_payment_form.span_processing_button_text.payment',
                )
              : translate(
                  'components.payment.stripe_payment_form.stripe_payment_form.span_processing_button_text.copy',
                )}
          </span>
          {loading && (
            <span className="af-processing-button-orbit" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          )}
        </button>
      </div>
    </form>
  );
}
